import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, ExitPage, Modal, Pill, StageChip } from "./ExitParts";
import { STAGE_SHORT, downloadCsv, exitApi, fmtDate } from "./exitApi";

const PAGE_SIZE = 50;
const EMPTY_FILTERS = { hr_approval: "no", status: "open", emp_code: "", name: "", pending_at: "", search: "", mine: false };

export default function ExitClearanceList() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState(null); // { stage, results? }

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "mine") { if (v) p.mine = 1; }
      else if (v) p[k] = v;
    });
    if (filters.status === "all") delete p.status;
    if (filters.hr_approval === "all") delete p.hr_approval;
    return p;
  }, [filters, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([exitApi.listCases(params), exitApi.summary()]);
      setRows(list.data || []);
      setTotal(list.total || 0);
      setSummary(sum.data);
    } catch {
      /* toast already shown */
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const stageColumns = useMemo(() => {
    const seen = [];
    rows.forEach((r) => r.stages.forEach((s) => { if (!seen.includes(s.stage_key)) seen.push(s.stage_key); }));
    const order = ["EMP", "RM", "SDM", "ADMIN", "IT", "FIN", "HR", "PAYROLL"];
    return order.filter((k) => seen.includes(k));
  }, [rows]);

  const selectedRows = rows.filter((r) => selected.includes(r.id));
  const bulkStages = [...new Set(selectedRows.flatMap((r) => r.pending_at))];
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const apply = (e) => { e.preventDefault(); setPage(1); setSelected([]); setFilters(draft); };
  const reset = () => { setDraft(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); setPage(1); setSelected([]); };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const runBulk = async () => {
    const ids = selectedRows.filter((r) => r.pending_at.includes(bulk.stage)).map((r) => r.id);
    try {
      const res = await exitApi.bulkSubmit(bulk.stage, ids);
      setBulk({ ...bulk, results: res.data });
      toast.success(res.message);
      setSelected([]);
      load();
    } catch { /* toast shown */ }
  };

  const exportCsv = async () => {
    try {
      const all = await exitApi.listCases({ ...params, page: 1, page_size: 500 });
      downloadCsv(`exit-clearance-${new Date().toISOString().slice(0, 10)}.csv`, all.data || [], [
        { label: "Exit ID", get: (r) => r.case_no },
        { label: "Employee Code", get: (r) => r.employee.code },
        { label: "Employee Name", get: (r) => r.employee.name },
        { label: "Department", get: (r) => r.department },
        { label: "Project", get: (r) => r.project },
        { label: "Exit Type", get: (r) => r.exit_type },
        { label: "Date of Resignation", get: (r) => r.date_resignation },
        { label: "Last Working Day", get: (r) => r.date_leaving },
        ...stageColumns.map((k) => ({ label: `${STAGE_SHORT[k]} Check`, get: (r) => r.stages.find((s) => s.stage_key === k)?.status || "" })),
        { label: "Pending At", get: (r) => r.pending_at.join(" / ") },
        { label: "Status", get: (r) => r.status },
      ]);
    } catch { /* toast shown */ }
  };

  return (
    <ExitPage
      title="Exit Clearance"
      actions={
        <>
          <button className="exitwf-btn ghost" onClick={() => navigate("/exit-clearance/masters")}>Masters</button>
          <button className="exitwf-btn" onClick={() => navigate("/exit-clearance/new")}>+ Initiate Exit</button>
        </>
      }
    >
      <div className="exitwf-stats">
        <div className="stat"><b>{summary?.open ?? "–"}</b><span>Open exits</span></div>
        <button className="stat clickable" onClick={() => { const f = { ...EMPTY_FILTERS, hr_approval: "all", mine: true }; setDraft(f); setFilters(f); setPage(1); }}>
          <b>{summary?.pending_with_me ?? "–"}</b><span>Pending with me</span>
        </button>
        <div className={`stat ${summary?.sla_breached ? "bad" : ""}`}><b>{summary?.sla_breached ?? "–"}</b><span>SLA breached</span></div>
        <div className="stat"><b>{summary?.closed ?? "–"}</b><span>Completed</span></div>
      </div>

      <Card title="Search for Employee Exit">
        <form className="exitwf-grid" onSubmit={apply}>
          <div className="exitwf-field">
            <label htmlFor="exf-hr">HR Approval</label>
            <select id="exf-hr" className="form-select" value={draft.hr_approval} onChange={(e) => setDraft({ ...draft, hr_approval: e.target.value })}>
              <option value="no">No</option><option value="yes">Yes</option><option value="all">All</option>
            </select>
          </div>
          <div className="exitwf-field">
            <label htmlFor="exf-status">Status</label>
            <select id="exf-status" className="form-select" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
              <option value="open">Open</option><option value="closed">Completed</option><option value="cancelled">Cancelled</option><option value="all">All</option>
            </select>
          </div>
          <div className="exitwf-field">
            <label htmlFor="exf-code">Employee Code</label>
            <input id="exf-code" className="form-control" value={draft.emp_code} onChange={(e) => setDraft({ ...draft, emp_code: e.target.value })} />
          </div>
          <div className="exitwf-field">
            <label htmlFor="exf-name">Employee Name</label>
            <input id="exf-name" className="form-control" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div className="exitwf-field">
            <label htmlFor="exf-pending">Pending At</label>
            <select id="exf-pending" className="form-select" value={draft.pending_at} onChange={(e) => setDraft({ ...draft, pending_at: e.target.value })}>
              <option value="">Any</option>
              {Object.entries(STAGE_SHORT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <label className="exitwf-check">
            <input type="checkbox" checked={draft.mine} onChange={(e) => setDraft({ ...draft, mine: e.target.checked })} /> Only pending with me
          </label>
          <div className="exitwf-actions span-all">
            <button type="button" className="exitwf-btn ghost" onClick={reset}>Reset</button>
            <button type="submit" className="exitwf-btn">Search</button>
          </div>
        </form>
      </Card>

      <Card title="Search Results">
        <div className="exitwf-toolbar">
          <input
            className="form-control exitwf-search" placeholder="Search by name, code, exit ID, project"
            value={draft.search} onChange={(e) => setDraft({ ...draft, search: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") apply(e); }}
          />
          <button className="exitwf-btn" disabled={!bulkStages.length} onClick={() => setBulk({ stage: bulkStages[0] })}>
            Submit Bulk{selected.length ? ` (${selected.length})` : ""}
          </button>
          <span className="spacer" />
          <button className="exitwf-btn ghost" onClick={exportCsv}>Export</button>
        </div>
        <div className="exitwf-table-wrap">
          <table className="exitwf-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" aria-label="Select all on page"
                    checked={rows.length > 0 && rows.every((r) => selected.includes(r.id))}
                    onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.id) : [])} />
                </th>
                <th>Exit ID</th><th>Employee Code</th><th>Employee Name</th><th>Department</th><th>Project</th>
                <th>Exit Type</th><th>Date of Resignation</th><th>Last Working Day</th>
                {stageColumns.map((k) => <th key={k}>{STAGE_SHORT[k]} Check</th>)}
                <th>Pending At</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/exit-clearance/${r.id}`)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${r.case_no}`} checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="link">{r.case_no}</td>
                  <td>{r.employee.code}</td>
                  <td>{r.employee.name}</td>
                  <td>{r.department}</td>
                  <td className="clip" title={r.project}>{r.project}</td>
                  <td>{r.exit_type}</td>
                  <td>{fmtDate(r.date_resignation)}</td>
                  <td>{fmtDate(r.date_leaving)}</td>
                  {stageColumns.map((k) => {
                    const s = r.stages.find((x) => x.stage_key === k);
                    return <td key={k}>{s ? <StageChip status={r.status === "open" ? s.status : s.status === "open" ? "waiting" : s.status} slaState={s.sla_state} /> : ""}</td>;
                  })}
                  <td>
                    {r.status === "closed" ? <Pill kind="done">Completed</Pill>
                      : r.status === "cancelled" ? <Pill>Cancelled</Pill>
                      : r.pending_at.map((k) => STAGE_SHORT[k]).join(" / ")}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={11 + stageColumns.length} className="empty">{loading ? "Loading…" : "No exits match these filters."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="exitwf-pager">
          <span>{total ? `${(page - 1) * PAGE_SIZE + 1} to ${Math.min(total, page * PAGE_SIZE)} of ${total}` : "0 of 0"}</span>
          <button className="exitwf-btn ghost sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
          <span>Page {page} of {pages}</span>
          <button className="exitwf-btn ghost sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>›</button>
        </div>
      </Card>

      {bulk && (
        <Modal
          title="Bulk submit"
          onClose={() => setBulk(null)}
          footer={
            bulk.results ? <button className="exitwf-btn" onClick={() => setBulk(null)}>Close</button> : (
              <>
                <button className="exitwf-btn ghost" onClick={() => setBulk(null)}>Cancel</button>
                <button className="exitwf-btn" onClick={runBulk}>Submit {STAGE_SHORT[bulk.stage]} stage</button>
              </>
            )
          }
        >
          {!bulk.results ? (
            <>
              <div className="exitwf-field">
                <label htmlFor="exf-bulk-stage">Stage to clear</label>
                <select id="exf-bulk-stage" className="form-select" value={bulk.stage} onChange={(e) => setBulk({ stage: e.target.value })}>
                  {bulkStages.map((k) => <option key={k} value={k}>{STAGE_SHORT[k]}</option>)}
                </select>
              </div>
              <p className="exitwf-hint">
                {selectedRows.filter((r) => r.pending_at.includes(bulk.stage)).length} selected exit(s) are pending at this stage.
                Values already saved on each exit are kept; blank fields take the standard clearance values set in Masters.
                Exits where you are not the approver for this stage are skipped.
              </p>
            </>
          ) : (
            <ul className="exitwf-results">
              {bulk.results.map((r) => (
                <li key={r.id} className={r.ok ? "ok" : "bad"}>
                  <b>{r.case_no || r.id}</b> {r.ok ? "Cleared" : r.message}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </ExitPage>
  );
}
